import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, ModalController, NavController, PopoverController } from '@ionic/angular';
import { ColumnMode, SelectionType } from '@swimlane/ngx-datatable';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { ComposePage } from 'src/app/components/compose/compose.page';
import { routes } from 'src/app/constants';
import { createCommunityRecipient, downloadCSV, isDev, Recipient } from 'src/app/utils/utils';
import { CommunityMember, Customer } from 'src/models';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';

@Component({
  selector: 'app-customer-admin-members',
  templateUrl: './customer-admin-members.page.html',
  styleUrls: ['./customer-admin-members.page.scss'],
})
export class CustomerAdminMembersPage implements OnInit {
  customer: any;
  customerId: string;

  searchText: string;
  members: any[] = [];
  filteredMembers: any[] = [];
  selectedMembers: any[] = [];
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  memberTypeFilter: 'all' | 'agents' | 'customers' = 'all';
  isSuperAdmin: boolean = false;
  isDev: boolean = false;
  slackChannelId: string = '';

  constructor(
    router: Router,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private alertCtrl: AlertController,
    private navController: NavController,
    private msgSrvc: MessageService,
    public authService: AuthService,
    private searchablePopoverService: SearchablePopoverService,
    private customersService: CustomersService,
    private slackService: SlackService,
  ) {
    this.customerId = router.url.split('/')[3];
  }

  async ngOnInit() {
    this.getCustomer();
    this.getMembers();
    this.isSuperAdmin = this.authService.isSuperAdmin;
    this.isDev = isDev();
  }

  async getMembers() {
    if (!this.customerId) {
      return;
    }

    const communityMembers = await this.customersService.getFollowers(this.customerId);

    this.members = communityMembers;
    this.filteredMembers = communityMembers;

    this.handleFilters();
  }

  syncChannel() {
    if (this.slackChannelId) {
      this.slackService.syncChannel(this.slackChannelId);
      this.msgSrvc.show('Submitted a background task to sync the channel!');
    } else {
      this.msgSrvc.showError('missing slackChannelId!');
    }
  }

  async changeRole(member: CommunityMember) {
    const options: ActionDropdownType[] = [
      {
        icon: member.isExternal ? 'enter-outline' : 'exit-outline',
        label: member.isExternal ? 'Change to "Agent"' : 'Change to "Customer"',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (i) => {
          popover.dismiss();

          if (i === 0) {
            await this.customersService.upsertCustomerMember({
              customerId: member.communityId,
              userId: member.userId,
              isExternal: !member.isExternal,
            });
            this.getMembers();
          }
        },
      },
      backdropDismiss: true,
      keyboardClose: true,
      event,
    });

    popover.present();
  }

  addAgent() {
    this.searchablePopoverService.channelMembers({
      event,
      customerId: this.customerId,
      callback: async (id: string, _name: string, object: any) => {
        this.isLoading = true;
        try {
          await this.customersService.upsertCustomerMember({
            customerId: this.customerId,
            userId: id,
            isExternal: false,
          });
          this.getMembers();

          this.isLoading = false;
        } catch (e) {
          this.msgSrvc.showError(e);
          this.isLoading = false;
        }
      },
    });
  }

  downloadMembers() {
    const headers = ['name', 'email', 'phoneNumber', 'notes', 'createdAt'];

    downloadCSV(
      this.members.map((communityMember) => ({
        name: communityMember.user?.name,
        email: communityMember.user?.email,
        phoneNumber: communityMember.user?.phoneNumber,
        notes: communityMember.notes,
        createdAt: communityMember.createdAt,
      })),
      headers,
      `member-list-${this.customerId}`,
    );
  }

  async sendMessage(emails?: (string | Recipient)[]) {
    if (!emails) {
      if (this.selectedMembers.length) {
        emails = this.selectedMembers.map((communityMember) => communityMember.user?.email).filter((e) => !!e);
      } else {
        emails = [createCommunityRecipient(this.customer)];
      }
    }

    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        communityId: this.customerId,
        emails,
        showTemplates: true,
      },
      cssClass: 'modal-large',
    });
    modal.present();
  }

  /*
  async sendSMS() {
    const list = this.selectedMembers.length ? this.selectedMembers : this.members;
    const phoneNumbers = list.map((communityMember) => communityMember.user?.phoneNumber).filter((num) => !!num);

    if (!phoneNumbers.length) {
      this.msgSrvc.show("These members don't have any phone numbers");
      return;
    }

    const confirm = await this.alertCtrl.create({
      header: `Send SMS`,
      subHeader: `${phoneNumbers.length} recipient${phoneNumbers.length === 1 ? '' : 's'}`,
      inputs: [
        {
          type: 'textarea',
          placeholder: 'Enter message (up to 320 characters)',
          attributes: {
            maxLength: 320,
          },
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Send',
          handler: async (data: any[]) => {
            let message = data[0];

            if (!message) {
              return;
            }

            // Send the SMS
            message = `From ${this.authService.user.displayName}:\n${message}\n(Do not reply)`;
            const user = this.authService.user;

            const textData: any = {
              createdBy: this.authService.userId,
              creatorName: user.displayName,
              to: phoneNumbers,
              message,
              tenantId: this.authService.tenantId,
              organizerId: this.customerId,
            };

            const sendTextResponse = this.apiService.post(`/texts`, textData);

            sendTextResponse.subscribe(
              async () => {
                await this.msgSrvc.show('Message sent!');
                this.isLoading = false;
              },
              async (err) => {
                await this.msgSrvc.showError(err);
                this.isLoading = false;
              },
              () => {
                this.isLoading = false;
              }
            );
          },
        },
      ],
    });
    confirm.present();
  }
  */

  async showMemberOptions() {
    const options = [
      {
        label: 'Mark as channel customers',
        icon: 'exit-outline',
      },
      {
        label: 'Mark as agents',
        icon: 'enter-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (index: number) => {
          popover.dismiss();
          switch (index) {
            case 0: {
              const confirm = await this.alertCtrl.create({
                header: 'Mark members as internal customers',
                message:
                  'The selected members will be marked as customers, and new messages will create conversations. Do you wish to continue?',
                buttons: [
                  {
                    text: 'Nevermind',
                    handler: () => {},
                  },
                  {
                    text: 'Confirm',
                    handler: () => {
                      this.markMembersAsInternalCustomers();
                    },
                  },
                ],
              });
              confirm.present();

              break;
            }
            case 1: {
              const confirm = await this.alertCtrl.create({
                header: 'Mark members as agents',
                message:
                  'The selected members will be marked as agents and be able to respond to conversations. Do you wish to continue?',
                buttons: [
                  {
                    text: 'Nevermind',
                    handler: () => {},
                  },
                  {
                    text: 'Confirm',
                    handler: () => {
                      this.markMembersAsTeamMembers();
                    },
                  },
                ],
              });
              confirm.present();

              break;
            }
            default:
              break;
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async markMembersAsInternalCustomers() {
    this.isLoading = true;

    try {
      const userIds = [];

      for (const member of this.selectedMembers) {
        if (!member.user?.isExternal) {
          userIds.push(member.userId);
        }
      }

      await this.customersService.bulkUpdateCustomerMembers({
        customerId: this.customerId,
        userIds,
        isExternal: true,
      });

      this.selectedMembers = [];

      await this.getMembers();
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async markMembersAsTeamMembers() {
    this.isLoading = true;

    try {
      const userIds = [];

      for (const member of this.selectedMembers) {
        if (!member.user?.isExternal) {
          userIds.push(member.userId);
        }
      }

      await this.customersService.bulkUpdateCustomerMembers({
        customerId: this.customerId,
        userIds,
        isExternal: false,
      });

      this.selectedMembers = [];

      await this.getMembers();
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async editMemberNotes(member: any) {
    const confirm = await this.alertCtrl.create({
      header: `Notes for ${member.user?.name}`,
      inputs: [
        {
          type: 'textarea',
          placeholder: 'Enter notes here',
          value: member.notes,
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Save',
          handler: async (data: any[]) => {
            this.isLoading = true;

            const notes = data[0];

            await this.customersService.upsertCustomerMember({
              customerId: this.customerId,
              userId: member.userId,
              notes,
            });

            await this.getMembers();

            this.isLoading = false;
          },
        },
      ],
    });
    confirm.present();
  }

  handleFilters() {
    const term = this.searchText ? this.searchText.toLowerCase() : '';
    this.filteredMembers = [];

    for (const communityMember of this.members) {
      if (
        communityMember.user?.name?.toLowerCase().includes(term) ||
        communityMember.user?.email?.toLowerCase().startsWith(term)
      ) {
        if (
          this.memberTypeFilter === 'all' ||
          (this.memberTypeFilter === 'agents' && !communityMember.isExternal) ||
          (this.memberTypeFilter === 'customers' && communityMember.isExternal)
        ) {
          this.filteredMembers.push(communityMember);
        }
      }
    }
  }

  // async removeSelectedMembers() {
  //   this.isLoading = true;

  //   try {
  //     const userIds = [];

  //     for (const member of this.selectedMembers) {
  //       userIds.push(member.userId);
  //     }

  //     await this.customersService.bulkUpdateCustomerMembers({
  //       customerId: this.customerId,
  //       userIds,
  //       following: false,
  //     });

  //     this.selectedMembers = [];

  //     await this.getMembers();
  //   } catch (error) {
  //     console.error(error);
  //   } finally {
  //     this.isLoading = false;
  //   }
  // }

  viewProfile(guest: any) {
    if (guest.userId) {
      this.navController.navigateForward(
        `/${routes.PEOPLE}${guest.user?.isExternal ? '/external' : ''}/${guest.userId}`,
      );
    }
  }

  onSelect({ selected }) {
    this.selectedMembers.splice(0, this.selectedMembers.length);
    this.selectedMembers.push(...selected);
  }

  async getCustomer() {
    const customer = await this.customersService.get(this.customerId);

    this.customer = customer;
    this.slackChannelId = this.customer.slackChannelId;
  }
}
